import { Box, Input, MenuItem, Popover, Select, SxProps } from '@mui/material';
import { KeyboardArrowDown } from '@mui/icons-material';
import { differenceInCalendarDays } from 'date-fns';
import moment from 'moment';
import { FC, useEffect, useRef, useState } from 'react';
import { Range } from 'react-date-range';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { CustomChartRangeSelection } from '../../../common/model/type/chart-range-selection.type';
import { DateRange } from '../../../common/model/type/date-range.type';
import { CalendarDayStatusUtils } from '../../../common/utils/services/calendar-day-status-utils';
import DateUtils from '../../../common/utils/services/date-utils';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';
import breakpoints from '../../../design-system/breakpoints';
import OvDayStatusHighlight from '../atoms/OvDayStatusHighlight';
import OvDateRangePicker from './OvDateRangePicker';
import { DailyData } from '../../../common/model/dto/daily-data';

const useStyles = (): { [key: string]: SxProps } => ({
  selectRoot: {
    backgroundColor: Colours.WHITE,
    borderRadius: '0.5rem !important',
    padding: '0',
  },
  selectIcon: {
    marginRight: 3,
    color: Colours.OV_BASE,
  },
  menuItemRoot: {
    height: '2.5rem',
  },
});

const PRESET_DAYS = {
  '30': 30,
  '60': 60,
  '90': 90,
} as const;

const OvUserChartsDaterangeSelector: FC<{
  selection: CustomChartRangeSelection;
  defaultText: string;
  dateRange: DateRange;
  onSelectionChange: (selection: CustomChartRangeSelection) => void;
  /** Daily data shown in the preview calendar for day-status highlighting. */
  dailyDataInCalendar: DailyData[];
  /** Requests the daily data for the given preview-calendar visible range. */
  onCalendarRangeChange: (startDate: Date, endDate: Date) => void;
  embedded?: boolean;
}> = ({
  selection,
  defaultText,
  dateRange,
  onSelectionChange,
  dailyDataInCalendar,
  onCalendarRangeChange,
  embedded = false,
}) => {
  const { t } = useTranslation();
  const styleClasses = useStyles();
  const [popoverAnchorEl, setPopoverAnchorEl] = useState<any | null>(null);
  const selectRef = useRef();
  const [selectedRange, setSelectedRange] = useState<Range[]>([
    {
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      key: 'selection',
    },
  ]);
  const selectionValue =
    selection.type === 'preset' ? String(selection.days) : 'custom';

  const isCycleRangePickerOpened = Boolean(popoverAnchorEl);
  const popoverId = isCycleRangePickerOpened ? 'simple-popover' : undefined;

  useEffect(() => {
    if (!isCycleRangePickerOpened) {
      setSelectedRange([
        {
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          key: 'selection',
        },
      ]);
    }
  }, [dateRange.endDate, dateRange.startDate, isCycleRangePickerOpened]);

  useEffect(() => {
    if (isCycleRangePickerOpened) {
      const now = new Date();

      const firstVisibleDay = moment(selectedRange[0]?.startDate ?? now)
        .startOf('month')
        .subtract(20, 'days') // 7days needed because of the layout of the calendar, and another 10 day to count post ovulation days
        .toDate();
      const lastVisibleDay = moment(selectedRange[0]?.endDate ?? now)
        .add(1, 'month')
        .endOf('month')
        .add(7, 'days')
        .toDate();

      onCalendarRangeChange(firstVisibleDay, lastVisibleDay);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCycleRangePickerOpened]);

  const handleNavigationBetweenMonths = (date: Date) => {
    const firstVisibleDay = moment(date)
      .startOf('month')
      .subtract(20, 'days') // 7days needed because of the layout of the calendar, and another 10 day to count post ovulation days
      .toDate();
    const lastVisibleDay = moment(date)
      .add(1, 'month')
      .endOf('month')
      .add(7, 'days')
      .toDate();

    onCalendarRangeChange(firstVisibleDay, lastVisibleDay);
  };

  const handleCloseCycleRangePicker = () => {
    setPopoverAnchorEl(null);
  };

  const onChangeCycleRange = (selectionRange: Range[]) => {
    setSelectedRange(selectionRange);
  };

  const onShownDateChanged = (date: Date) => {
    if (date) {
      handleNavigationBetweenMonths(date);
    }
  };

  const getLogIndexFromDate = (calendarDate: Date): number => {
    let logIndex = -1;

    if (dailyDataInCalendar.length) {
      dailyDataInCalendar.forEach((dailyData, index) => {
        const dailyDataDate: Date = moment(
          dailyData?.day,
          'YYYY-MM-DD'
        ).toDate();

        if (DateUtils.areSameDays(dailyDataDate, calendarDate)) {
          logIndex = index;
        }
      });
    }

    return logIndex;
  };

  const handleOnClick = () => {
    const { startDate, endDate } = selectedRange[0];

    if (startDate && endDate) {
      onSelectionChange({ type: 'range', startDate, endDate });
      setPopoverAnchorEl(null);
    }
  };

  return (
    <Container $embedded={embedded}>
      <Select
        ref={selectRef}
        value={selectionValue}
        onChange={(e) => {
          const value = e.target.value as string;

          if (value === 'custom') {
            return;
          }

          const days = PRESET_DAYS[value as keyof typeof PRESET_DAYS];
          if (days) {
            onSelectionChange({ type: 'preset', days });
          }
        }}
        input={<Input disableUnderline fullWidth color="secondary" />}
        IconComponent={KeyboardArrowDown}
        renderValue={() => (
          <Box ml={2} mr={1}>
            <SelectionText>{defaultText}</SelectionText>
          </Box>
        )}
        MenuProps={{
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'center',
          },
        }}
      >
        <MenuItem value="30" sx={{ ...styleClasses.menuItemRoot }}>
          <SelectionText>
            {t('dashboard.lastXDays', { numOfDays: 30 })}
          </SelectionText>
        </MenuItem>
        <MenuItem value="60" sx={{ ...styleClasses.menuItemRoot }}>
          <SelectionText>
            {t('dashboard.lastXDays', { numOfDays: 60 })}
          </SelectionText>
        </MenuItem>
        <MenuItem value="90" sx={{ ...styleClasses.menuItemRoot }}>
          <SelectionText>
            {t('dashboard.lastXDays', { numOfDays: 90 })}
          </SelectionText>
        </MenuItem>
        <MenuItem
          value="custom"
          onClick={() => {
            setPopoverAnchorEl(selectRef.current);
            return;
          }}
          sx={{ ...styleClasses.menuItemRoot }}
        >
          <SelectionText>{t('dashboard.customDateRange')}</SelectionText>
        </MenuItem>
      </Select>
      <StyledPopover
        id={popoverId}
        open={isCycleRangePickerOpened}
        anchorEl={popoverAnchorEl}
        onClose={handleCloseCycleRangePicker}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <OvDateRangePicker
          ranges={selectedRange}
          onChange={(item) => onChangeCycleRange([item.selection])}
          onShownDateChange={(date) => onShownDateChanged(date)}
          showDateDisplay={false}
          moveRangeOnFirstSelection={false}
          months={2}
          direction="horizontal"
          preventSnapRefocus={true}
          showPreview={true}
          showMonthAndYearPickers={false}
          rangeColors={['#dfdfdf']}
          dayContentRenderer={(date) => (
            <OvDayStatusHighlight
              tryingToConceiveStatus={CalendarDayStatusUtils.calculateDayStatus(
                dailyDataInCalendar,
                getLogIndexFromDate(date)
              )}
            >
              {date.getDate()}
            </OvDayStatusHighlight>
          )}
        />
        <StyledButton
          onClick={handleOnClick}
          disabled={
            selectedRange?.[0]?.startDate === undefined ||
            selectedRange?.[0]?.endDate === undefined ||
            differenceInCalendarDays(
              selectedRange[0].endDate,
              selectedRange[0].startDate
            ) < 1
          }
        >
          {t('common.submit')}
        </StyledButton>
      </StyledPopover>
    </Container>
  );
};

export default OvUserChartsDaterangeSelector;

const Container = styled.div<{ $embedded: boolean }>`
  width: ${({ $embedded }) => ($embedded ? 'auto' : '30%')};
  flex: ${({ $embedded }) => ($embedded ? '1' : 'initial')};
  min-width: 15.625rem;
  box-shadow: ${({ $embedded }) =>
    $embedded ? 'none' : Variables.boxShadow.defaultBox};
  border-radius: ${({ $embedded }) =>
    $embedded ? Variables.borderRadius.LARGE : Variables.borderRadius.SMALL};
  margin: ${({ $embedded }) =>
    $embedded ? '0.5rem 1rem 0.5rem 0.5rem' : '0 0 3.25rem'};
  background-color: ${Colours.WHITE};

  @media (max-width: ${breakpoints.md}) {
    width: ${({ $embedded }) => ($embedded ? 'auto' : '100%')};
    margin: ${({ $embedded }) => ($embedded ? '0.5rem 1rem' : '0 0 3.25rem')};
  }
`;

const SelectionText = styled.p`
  font-weight: bold;
  color: ${Colours.OV_BASE};
  font-size: ${Variables.fontSizes.MEDIUM};
  margin: 0.5rem 0;
  font-family: 'CentraNo2-Book', 'CentraNo2', Roboto, sans-serif; // honestly not sure why default styles not applied...
`;

const StyledPopover = styled(Popover)`
  && {
    .MuiPopover-paper {
      border-radius: 0.75rem;
      border: 1px solid ${Colours.OV_BASE};
      display: flex;
      flex-direction: column;
    }
  }
`;

const StyledButton = styled.button`
  background: none;
  width: 6.25rem;
  padding: 0.625rem 0.325rem;
  margin: auto;
  margin-bottom: 0.625rem;
  margin-top: 0.625rem;
  border-radius: 0.75rem;
  color: ${Colours.OV_BASE};
  font-weight: bold;
  border: 2px solid ${Colours.OV_BASE};
  cursor: pointer;

  &:hover {
    background-color: ${Colours.OV_WHITE_HOVER};
  }
`;
